import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTenantUsers, getTenantUserById, getTenantUserByEmail } from "../orgPanelApiCalls";
import { useStore } from '../appStore';
import { useEffect } from 'react';

const key = 'tenantUsers';
const emailUserKey = 'tenantUsersByEmail';

const useGetTenantUsersQuery = (portalAccountId) => {
    return useQuery({ queryKey: [key, portalAccountId], queryFn: () => getTenantUsers(portalAccountId) })
}
const useGetTenantUserByIdQuery = (userAccountId) => {
    const orgId = useStore((s) => s.CurrentOrganizationId);
    const qc = useQueryClient();

    const q = useQuery({
        queryKey: [key, userAccountId, orgId],                 // include all inputs
        enabled: userAccountId != null && orgId != null,       // allow 0 values
        queryFn: async () => {
            const data = await getTenantUserById(userAccountId, orgId);
            return data;
        },
    });

    // Success side-effects (runs after every successful fetch/refetch)
    useEffect(() => {
        if (q.isSuccess) {
            const listKey = [key, orgId];
            const usersList = qc.getQueryData(listKey);
            const dashboardUser = usersList.find((user) => user.accountId === q.data.accountId);
            if (dashboardUser?.userStatus !== q.data.userStatus) {
                qc.invalidateQueries(listKey);
            }
        }
    }, [q.dataUpdatedAt]); // updates on each successful fetch

    // Error side-effects (was onError)
    useEffect(() => {
        if (q.isError) {
            qc.invalidateQueries({ queryKey: [key, orgId], exact: true });
        }
    }, [q.isError, q.error, qc]);

    return q; // { data, isLoading, isError, refetch, ... }
}

const useGetTenantUserByEmailQuery = (email) => {
    const orgId = useStore((s) => s.CurrentOrganizationId);
    const qc = useQueryClient();

    const q = useQuery({
        queryKey: [emailUserKey, orgId, email],
        enabled: email != null,
        queryFn: async () => {
            const data = await getTenantUserByEmail(orgId, email);
            return data;
        },
        retry: false,
    });

    // Error side-effects (was onError)
    useEffect(() => {
        if (q.isError) {
            qc.invalidateQueries({ queryKey: [key, orgId, email], exact: true });
        }
    }, [q.isError, q.error, qc]);

    return q; // { data, isLoading, isError, refetch, ... }
}

export { useGetTenantUsersQuery, useGetTenantUserByIdQuery, useGetTenantUserByEmailQuery, key };
